import { ComponentRef, Injectable, Injector, StaticProvider } from '@angular/core';
import {
  AUTH_OVERLAY_DATA,
  AuthOverlayConfig,
} from '../models/auth-overlay-config.interface';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { AuthOverlayRef } from '../models/auth-overlay-ref.model';
import { ComponentPortal } from '@angular/cdk/portal';
import { AuthOverlayComponent } from '../portal/auth-overlay.component';
import { Subscription } from 'rxjs';

const DEFAULT_CONFIG: AuthOverlayConfig = {
  hasBackdrop: true,
  backdropClass: 'popsy-dialog-overlay',
  panelClass: 'popsy-overlay-panel',
};

@Injectable({
  providedIn: 'root',
})
export class AuthOverlayService {
  private subscriptions: Subscription;

  constructor(private readonly injector: Injector, private readonly overlay: Overlay) {
    this.subscriptions = new Subscription();
  }

  public clearSubscriptions(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  public open(config: AuthOverlayConfig = {}): AuthOverlayRef {
    this.clearSubscriptions();
    this.subscriptions = new Subscription();

    // Override default configuration
    const dialogConfig = {
      ...DEFAULT_CONFIG,
      ...config,
    };

    // Returns an OverlayRef which is a PortalHost
    const overlayRef = this.createOverlay(dialogConfig);

    // Instantiate remote control
    const dialogRef = new AuthOverlayRef(overlayRef);
    this.attachDialogContainer(overlayRef, dialogConfig, dialogRef);
    this.subscriptions.add(
      overlayRef
        .backdropClick()
        .subscribe({ next: () => dialogRef.close(), error: () => {} })
    );
    return dialogRef;
  }

  private createOverlay(config: AuthOverlayConfig): OverlayRef {
    const overlayConfig = this.getOverlayConfig(config);
    return this.overlay.create(overlayConfig);
  }

  private attachDialogContainer(
    overlayRef: OverlayRef,
    config: AuthOverlayConfig,
    dialogRef: AuthOverlayRef
  ) {
    const injector = this.createInjector(config, dialogRef);
    const containerPortal = new ComponentPortal<AuthOverlayComponent>(
      AuthOverlayComponent,
      null,
      injector
    );
    const containerRef: ComponentRef<AuthOverlayComponent> =
      overlayRef.attach(containerPortal);
    return containerRef.instance;
  }

  private createInjector(config: AuthOverlayConfig, dialogRef: AuthOverlayRef): Injector {
    const injectionTokens: Array<StaticProvider> = new Array(0);
    injectionTokens.push({ provide: AuthOverlayRef, useValue: dialogRef });
    injectionTokens.push({
      provide: AUTH_OVERLAY_DATA,
      useValue: config.data || {},
    });
    return Injector.create({
      providers: injectionTokens,
      parent: this.injector,
      name: 'auth-overlay',
    });
  }

  private getOverlayConfig(config: AuthOverlayConfig): OverlayConfig {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    return new OverlayConfig({
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      panelClass: config.panelClass,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy,
    });
  }
}
